import { createSlice, current } from "@reduxjs/toolkit";
import {
  defaultStep,
  getPriceRange,
  calculateHasSelectedValuesState
} from "./priceRangeSelectors";
import { hasFacets } from "./selectors";
import { getNewRangeState } from "./priceRangeReducerHelper";
import { mapAndInsertProducts } from "./product";
import config from "../../../config/common";
import {
  extendOffsetLowerBound,
  extendOffsetUpperBound,
  getOffsetFromQuery
} from "./offset";
import getPreviousPageNumber from "./getPreviousPageNumber";
import getNextPageNumber from "./getNextPageNumber";
import {
  anchorProduct,
  updateImageGalleryOnboardDismissed,
  loadingPreviousProducts,
  previousProductsLoaded,
  loadingMoreProducts,
  moreProductsLoaded,
  requestProducts,
  receiveProducts,
  updateMultiFacet,
  dismissFloorFilter,
  updateRangeFacet,
  updateSortFacet,
  updatePersonalisationParams,
  setAdditionalQueryParams,
  updateAdditionalQueryParams,
  clearRangeFacet,
  clearSelections,
  setRangeValues,
  undoRangeValues,
  setNewSearch,
  clearNewSearch,
  clearOnViewBeacon,
  clearOnLoadBeacon,
  restoreOldSelectedFacets,
  refreshAdvertisementBeacons
} from "./actionCreators";

const pageSize = config.api.product.search.pageSize;

const mergeSelectedFacets = (existing, incoming) => {
  const map = new Map();

  const addFacetsToMap = (facets, mapToUpdate) => {
    facets.forEach(facet => {
      if (facet && facet.facetId && facet.valueId) {
        mapToUpdate.set(`${facet.facetId}:${facet.valueId}`, facet);
      }
    });
  };

  addFacetsToMap(existing, map);
  const arr = Array.isArray(incoming) ? incoming : [incoming];
  addFacetsToMap(arr, map);

  return Array.from(map.values());
};

const defaultPageSize = 72;

export const initialState = {
  searchTerm: "",
  redirectUrl: "",
  products: [],
  facets: [],
  selectedFacets: [],
  selectedSort: {},
  error: null,
  isLoading: false,
  isNewSearch: true,
  itemCount: 0,
  offset: {
    initial: 0,
    lower: 0,
    upper: pageSize
  },
  isLoadingMore: false,
  isLoadingPrevious: false,
  showLoadMore: false,
  tileToFocus: {
    first: false,
    last: false,
    pageNumber: null
  },
  range: {
    currentprice: {
      selected: {
        current: null,
        temp: null
      },
      min: 0,
      max: 0,
      step: defaultStep,
      hasSelectedValues: false
    }
  },
  diagnostics: {
    recommendationsAnalytics: {}
  },
  searchPassMeta: {
    alternateSearchTerms: []
  },
  oldSelectedFacets: [],
  personalisationParams: {},
  pageSize: defaultPageSize,
  dismissedFloors: {}
};

const shouldShowLoadMore = (offset, itemCount) => offset.upper < itemCount;

const shouldShowLoadPrevious = offset => offset.lower > 0;

const hasFilterReturnedNoResults = (newItemCount, previousState) =>
  newItemCount === 0 && hasFacets({ search: previousState });

const filterCurrentPrice = selectedFacets =>
  selectedFacets.filter(filter => filter.facetId === "currentprice");

const getReceiveSearchDiagnostic = diagnostics => {
  if (CLIENT) {
    return diagnostics;
  }

  return {
    ...diagnostics,
    advertisementsAnalytics: null
  };
};

const mergeDiagnostics = (stateDiagnostics, payloadDiagnostics) => {
  if (!payloadDiagnostics) {
    return stateDiagnostics;
  }

  const filteredPayload = Object.fromEntries(
    Object.entries(payloadDiagnostics).filter(
      ([, value]) => value !== null && value !== undefined
    )
  );

  return { ...stateDiagnostics, ...filteredPayload };
};

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(anchorProduct, state => {
        state.isNewSearch = false;
      })
      .addCase(updateImageGalleryOnboardDismissed, (state, action) => {
        state.imageGalleryOnboardDismissed =
          action.payload.imageGalleryOnboardDismissed;
      })
      .addCase(loadingPreviousProducts, state => {
        state.isLoadingPrevious = true;
        state.isNewSearch = false;
      })
      .addCase(previousProductsLoaded, (state, action) => {
        if (action.error) {
          state.isLoadingPrevious = false;
          state.tileToFocus = { ...initialState.tileToFocus };

          return;
        }

        const stateSnapshot = current(state);
        const offset = extendOffsetLowerBound(stateSnapshot);
        const { products: existingProducts } = stateSnapshot;
        const { products } = mapAndInsertProducts({
          existingProducts,
          products: action.payload.products
        });

        state.offset = offset;
        state.tileToFocus = {
          first: false,
          last: true,
          pageNumber: getPreviousPageNumber(stateSnapshot)
        };
        state.products = products;
        state.diagnostics = mergeDiagnostics(
          state.diagnostics,
          action.payload.diagnostics
        );
        state.isLoadingPrevious = false;
        state.showLoadPrevious = shouldShowLoadPrevious(offset);
        state.error = null;
      })
      .addCase(loadingMoreProducts, state => {
        state.isLoadingMore = true;
        state.isNewSearch = false;
      })
      .addCase(moreProductsLoaded, (state, action) => {
        if (action.error) {
          state.isLoadingMore = false;
          state.tileToFocus = { ...initialState.tileToFocus };

          return;
        }

        const stateSnapshot = current(state);
        const { products: existingProducts } = stateSnapshot;
        const offset = extendOffsetUpperBound(stateSnapshot, action.payload);
        const { products } = mapAndInsertProducts({
          existingProducts,
          products: action.payload.products,
          insertionIndex: stateSnapshot.products.length
        });

        state.offset = offset;
        state.tileToFocus = {
          first: true,
          last: false,
          pageNumber: getNextPageNumber(stateSnapshot)
        };
        state.products = products;
        state.diagnostics = mergeDiagnostics(
          state.diagnostics,
          action.payload.diagnostics
        );
        state.isLoadingMore = false;
        state.showLoadMore = shouldShowLoadMore(
          offset,
          action.payload.itemCount
        );
        state.error = null;
      })
      .addCase(requestProducts, (state, { payload }) => {
        state.query = payload.query;
        state.isLoading = true;
        state.isNewPage = payload.isNewPage;
        state.isNewSearch = true;
      })
      .addCase(receiveProducts, (state, { error, payload }) => {
        if (error) {
          state.isLoading = SERVER;
          state.tileToFocus = { ...initialState.tileToFocus };

          return;
        }

        const {
          itemCount,
          searchPassMeta,
          searchTerm,
          redirectUrl,
          query = {},
          isNewPage
        } = payload;
        const stateSnapshot = current(state);
        const offset = getOffsetFromQuery(stateSnapshot, payload);
        const diagnostics = getReceiveSearchDiagnostic(payload.diagnostics);

        const { products } = mapAndInsertProducts({
          existingProducts: [],
          products: payload.products
        });

        const facets = Array.isArray(payload.facets) ? [...payload.facets] : [];
        const preserveFacetsForFilterOut =
          !isNewPage && hasFilterReturnedNoResults(itemCount, stateSnapshot)
            ? {
                facets: stateSnapshot.facets,
                range: stateSnapshot.range,
                selectedFacets: filterCurrentPrice(
                  stateSnapshot.oldSelectedFacets
                )
              }
            : null;

        state.facets = facets;
        state.range = getNewRangeState(
          initialState,
          state,
          facets,
          query,
          isNewPage
        );
        state.products = products;
        state.isLoading = false;
        state.isNewPage = false;
        state.redirectUrl = redirectUrl;
        state.selectedFacets = isNewPage
          ? initialState.selectedFacets
          : (state.selectedFacets || []).filter(
              selected =>
                selected.facetId !== "currentprice" &&
                facets.some(facet => facet.id === selected.facetId)
            );
        state.itemCount = itemCount;
        state.offset = offset;
        state.tileToFocus = { ...initialState.tileToFocus };
        state.showLoadMore = shouldShowLoadMore(offset, itemCount);
        state.showLoadPrevious = shouldShowLoadPrevious(offset);
        state.diagnostics = diagnostics;
        state.searchPassMeta = searchPassMeta;
        state.searchTerm = searchTerm;
        state.error = null;
        state.oldSelectedFacets = [];
        state.pageSize = pageSize;

        if (preserveFacetsForFilterOut) {
          state.facets = preserveFacetsForFilterOut.facets;
          state.range = preserveFacetsForFilterOut.range;
          state.selectedFacets = preserveFacetsForFilterOut.selectedFacets;
        }
      })
      .addCase(updateMultiFacet, (state, { payload }) => {
        state.selectedFacets = mergeSelectedFacets(
          state.selectedFacets,
          payload
        );
      })
      .addCase(dismissFloorFilter, (state, { payload: floor }) => {
        state.dismissedFloors = {
          ...state.dismissedFloors,
          [floor]: true
        };
      })
      .addCase(updateRangeFacet, (state, { payload }) => {
        state.range.currentprice.selected.temp = payload.value;
        state.range.currentprice.hasSelectedValues = calculateHasSelectedValuesState(
          payload.value,
          state.range.currentprice
        );
        state.selectedFacets = state.selectedFacets.concat(
          getPriceRange(state).map(facet => ({
            facetId: facet.id,
            isSelected: true,
            valueId: facet.facetValues[0].id
          }))
        );
      })
      .addCase(updateSortFacet, (state, { payload }) => {
        state.selectedSort = { value: payload };
      })
      .addCase(updatePersonalisationParams, (state, { payload }) => {
        state.personalisationParams = {
          ...payload
        };
      })
      .addCase(setAdditionalQueryParams, (state, { payload }) => {
        state.additionalQueryParams = {
          ...payload
        };
      })
      .addCase(updateAdditionalQueryParams, (state, { payload }) => {
        state.additionalQueryParams = {
          ...state.additionalQueryParams,
          refine: payload.refine || ""
        };
      })
      .addCase(clearRangeFacet, state => {
        state.range.currentprice.selected.temp = {
          leftIndicator: state.range.currentprice.min,
          rightIndicator: state.range.currentprice.max
        };
        state.range.currentprice.hasSelectedValues = false;
        state.selectedFacets = state.selectedFacets.concat(
          getPriceRange(state).map(facet => ({
            facetId: facet.id,
            isSelected: false,
            valueId: facet.facetValues[0].id
          }))
        );
      })
      .addCase(clearSelections, state => {
        state.selectedSort = {};
        if (hasFilterReturnedNoResults(state.itemCount, state)) {
          state.selectedFacets = filterCurrentPrice(state.selectedFacets);

          return;
        }

        state.selectedFacets = initialState.selectedFacets;
        state.range.currentprice.selected.temp = null;
        state.range.currentprice.hasSelectedValues = calculateHasSelectedValuesState(
          state.range.currentprice.selected.current,
          state.range.currentprice
        );
      })
      .addCase(setRangeValues, (state, { payload }) => {
        const previousSelectedFacets = state.selectedFacets;
        state.oldSelectedFacets = previousSelectedFacets;
        state.selectedFacets = previousSelectedFacets.filter(
          facet => facet.facetId !== "currentprice"
        );
        state.range.currentprice.selected.previous =
          state.range.currentprice.selected.current;
        state.range.currentprice.selected.current = payload.values;
        state.range.currentprice.selected.temp = null;
        state.range.currentprice.hasSelectedValues = calculateHasSelectedValuesState(
          payload.values,
          state.range.currentprice
        );
      })
      .addCase(undoRangeValues, state => {
        if (state.range.currentprice.selected.previous) {
          state.range.currentprice.selected.current =
            state.range.currentprice.selected.previous;
          state.range.currentprice.selected.previous = null;
        }
      })
      .addCase(setNewSearch, state => {
        state.isNewSearch = true;
        state.additionalQueryParams = {};
      })
      .addCase(clearNewSearch, state => {
        state.isNewSearch = false;
      })
      .addCase(clearOnViewBeacon, state => {
        state.diagnostics.advertisementsAnalytics.placementBeacons.onViewBeacon = null;
      })
      .addCase(clearOnLoadBeacon, state => {
        state.diagnostics.advertisementsAnalytics.placementBeacons.onLoadBeacon = null;
      })
      .addCase(restoreOldSelectedFacets, state => {
        state.selectedFacets = [...state.oldSelectedFacets];
        state.oldSelectedFacets = [];
      })
      .addCase(
        refreshAdvertisementBeacons,
        (
          state,
          { payload: { sponsoredProducts, onLoadBeacon, onViewBeacon } }
        ) => {
          state.products = sponsoredProducts
            ? state.products.map(product => {
                if (product.isSponsored) {
                  const variantId = product.advertisement.variantId;
                  const sponsoredProduct = sponsoredProducts.find(
                    ({ ProductId }) => ProductId === variantId
                  );

                  if (sponsoredProduct) {
                    return {
                      ...product,
                      advertisement: {
                        ...product.advertisement,
                        onLoadBeacon: sponsoredProduct.OnLoadBeacon,
                        onViewBeacon: sponsoredProduct.OnViewBeacon,
                        onClickBeacon: sponsoredProduct.OnClickBeacon,
                        onBasketChangeBeacon:
                          sponsoredProduct.OnBasketChangeBeacon,
                        onWishlistBeacon: sponsoredProduct.OnWishlistBeacon
                      }
                    };
                  }
                }

                return product;
              })
            : state.products;
          state.diagnostics = {
            ...state.diagnostics,
            advertisementsAnalytics: {
              placementBeacons: {
                onLoadBeacon,
                onViewBeacon
              }
            }
          };
        }
      );
  }
});

export default searchSlice.reducer;
